// Vocabulary shared by every global-CRM surface: the icon and the tone of a
// team-defined type, a member's avatar, and the small derivations the page
// repeats.
import {
  Calendar,
  FileText,
  Flag,
  Github,
  Handshake,
  Lightbulb,
  Linkedin,
  type LucideIcon,
  Mail,
  MessageSquare,
  Phone,
  PhoneIncoming,
  Receipt,
  Repeat,
  Star,
  StickyNote,
  Video,
} from "lucide-react"

import type {
  CrmAccountDetail,
  CrmAccountRedirect,
  CrmColor,
  CrmIcon,
} from "@/nango/types/apps.autoleads/autoleads/Crm"

export const CRM_ICONS = {
  phone: Phone,
  phone_incoming: PhoneIncoming,
  video: Video,
  mail: Mail,
  linkedin: Linkedin,
  note: StickyNote,
  file_text: FileText,
  receipt: Receipt,
  github: Github,
  lightbulb: Lightbulb,
  calendar: Calendar,
  repeat: Repeat,
  handshake: Handshake,
  star: Star,
  flag: Flag,
  message: MessageSquare,
} as const satisfies Record<CrmIcon, LucideIcon>

export const CRM_ICON_KEYS = Object.keys(CRM_ICONS) as CrmIcon[]

// Each color is one of the theme's avatar tokens (a light tint with a dark
// foreground, re-tuned in dark mode), so a team-picked color can never break
// contrast. Handed to the element as two custom properties (`--crm-tint`,
// `--crm-ink`) that its classes read, the same way the board passes a lost
// motive's color: no color value is ever written in a class.
const COLOR_TOKEN: Record<Exclude<CrmColor, "neutral">, number> = {
  red: 1,
  amber: 3,
  green: 5,
  blue: 8,
  violet: 10,
}

export const CRM_COLOR_KEYS: CrmColor[] = [
  "neutral",
  "blue",
  "green",
  "amber",
  "red",
  "violet",
]

export type TintTokens = { tint: string; ink: string }

// Classes that paint an element from the tokens below.
export const TINT_CLASS = "bg-(--crm-tint) text-(--crm-ink)"

export function crmColorTokens(color: CrmColor): TintTokens {
  if (color === "neutral")
    return { tint: "var(--muted)", ink: "var(--muted-foreground)" }
  const index = COLOR_TOKEN[color]
  return { tint: `var(--avatar-${index})`, ink: `var(--avatar-${index}-fg)` }
}

const AVATAR_TOKENS = 12

// A member keeps the same tint everywhere it appears.
export function memberTokens(memberId: number): TintTokens {
  const index = (memberId % AVATAR_TOKENS) + 1
  return { tint: `var(--avatar-${index})`, ink: `var(--avatar-${index}-fg)` }
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return "?"
  const first = parts[0]?.[0] ?? ""
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : ""
  return (first + last).toUpperCase()
}

export function isAccountRedirect(
  detail: CrmAccountDetail | CrmAccountRedirect | undefined,
): detail is CrmAccountRedirect {
  return detail !== undefined && "merged_into_id" in detail
}

// "FR:123456789" -> "123 456 789", the way a SIREN is printed.
export function formatRegistryId(registryId: string): string {
  const [, number = registryId] = registryId.split(":")
  return /^\d{9}$/.test(number)
    ? `${number.slice(0, 3)} ${number.slice(3, 6)} ${number.slice(6)}`
    : number
}

export function addDays(base: Date, days: number): Date {
  const next = new Date(base)
  next.setDate(next.getDate() + days)
  return next
}
